import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

interface CurrencyInputProps {
  /**
   * Valor em reais (ex.: 1234.56). `undefined` e `null` são o campo vazio - o
   * `null` entra na assinatura porque campo de preço opcional costuma vir
   * `number | null` da API, e obrigar cada tela a escrever `?? undefined`
   * convida ao `?? 0`, que é justamente o que não pode acontecer.
   */
  value?: number | null;
  onChange: (value: number | undefined) => void;
  /**
   * Faz "tudo zero" ser o campo vazio: digitar `0`, ou apagar até sobrar
   * `0,00`, devolve `undefined` em vez de `0`.
   *
   * ## Por que isto é opt-in, e por que precisa existir (card #218)
   *
   * O parser junta os dígitos e divide por 100. Sem esta opção, apagar com
   * Backspace **nunca esvazia o campo**: `0,01` vira o texto `0,0`, cujos
   * dígitos são `00`, que viram `0`, que a máscara reescreve como `0,00` - e
   * a próxima tecla repete o ciclo para sempre. O único jeito de limpar é
   * selecionar tudo e apagar de uma vez, que quase ninguém faz. Ou seja: o
   * componente transformava vazio em `0,00`, exatamente o que o §2 do spec
   * 033 proíbe na origem - item sem preço tem de mandar `price: null`, e
   * `R$ 0,00` numa página pública é afirmação falsa sobre o preço.
   *
   * É opt-in porque `0` é valor legítimo em outras telas: o plano gratuito
   * (`PlansPage`) e a experiência gratuita (`ExperienceFormModal`) guardam
   * preço zero de propósito - o Kardume não cobra pela utilização básica.
   * Ligar isto para todo mundo tiraria deles a capacidade de digitar o zero.
   *
   * Onde `0` não é um valor possível - preço opcional, com o core recusando
   * `price <= 0` -, ligue: aí "zero" e "vazio" são a mesma coisa, e a tecla
   * Backspace volta a funcionar.
   */
  allowEmpty?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  className?: string;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  label?: string;
  helpText?: string;
  error?: string;
  onBlur?: () => void;
  containerStyle?: React.CSSProperties;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Req = styled.span`
  color: ${(p) => p.theme.error};
  margin-left: 4px;
`;

const InputWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) => (p.$disabled ? p.theme.surfaceAlt : p.theme.surface)};
  border: 1px solid ${(p) => (p.$hasError ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  padding: 0 0.75rem;
  min-height: 42px;
  box-sizing: border-box;
  transition: all 0.2s;

  &:focus-within {
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }

  .symbol {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${(p) => p.theme.textMuted};
    margin-right: 0.5rem;
    user-select: none;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 0.6rem 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  color: ${(p) => p.theme.textPrimary};
  outline: none;

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const HelpText = styled.small<{ $isError?: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$isError ? p.theme.error : p.theme.textMuted)};
`;

/**
 * BRL currency input (pt-BR): displays 1.234,56, parses by extracting digits and
 * dividing by 100, and allows clearing (undefined). `value`/`onChange` are in reais.
 *
 * This is THE input for money across both apps - never `<TextInput type="number">`.
 * A number input asks for "12.5" and shows a spinner: the wrong keyboard on
 * mobile, the wrong decimal separator for pt-BR, and no thousands grouping, so
 * "1250" reads as twelve-fifty or one-two-five-zero depending on the reader.
 * Here the user types digits and always sees reais and centavos.
 *
 * Ported from `leo-delivery-monorepo/packages/ui/src/CurrencyInput.tsx`, with the
 * theme tokens and the `containerStyle` prop of this design system.
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  allowEmpty = false,
  placeholder = '0,00',
  disabled,
  required,
  min,
  max,
  className,
  id,
  name,
  autoFocus,
  label,
  helpText,
  error,
  onBlur,
  containerStyle,
}) => {
  const formatter = useMemo(
    () => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    []
  );

  const [display, setDisplay] = useState<string>('');

  useEffect(() => {
    setDisplay(typeof value === 'number' && !Number.isNaN(value) ? formatter.format(value) : '');
  }, [value, formatter]);

  const parseToNumber = (text: string): number | undefined => {
    const digits = (text || '').replace(/\D/g, '');
    if (!digits) return undefined;
    // Ver `allowEmpty`: só-zeros é o campo vazio, e é o que devolve a tecla
    // Backspace para quem precisa limpar um preço opcional.
    if (allowEmpty && /^0+$/.test(digits)) return undefined;
    const cents = parseInt(digits, 10);
    return Number.isNaN(cents) ? undefined : cents / 100;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let next = parseToNumber(e.target.value);
    if (typeof next === 'number') {
      if (typeof min === 'number' && next < min) next = min;
      if (typeof max === 'number' && next > max) next = max;
    }
    setDisplay(next !== undefined ? formatter.format(next) : '');
    onChange(next);
  };

  const handleBlur = () => {
    setDisplay(typeof value === 'number' ? formatter.format(value) : '');
    onBlur?.();
  };

  return (
    <Container className={className} style={containerStyle}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <Req>*</Req>}
        </Label>
      )}
      <InputWrapper $disabled={disabled} $hasError={!!error}>
        <span className="symbol">R$</span>
        <StyledInput
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          autoFocus={autoFocus}
        />
      </InputWrapper>
      {(error || helpText) && <HelpText $isError={!!error}>{error || helpText}</HelpText>}
    </Container>
  );
};

export default CurrencyInput;
